import React, { useState } from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Button } from "@/components/ui/button";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import MemberProfileModal from "@/components/MemberProfileModal";
import WebhookBotProfileModal from "@/components/WebhookBotProfileModal";
import CircatronBotProfileModal from "@/components/CircatronBotProfileModal";
import { getFullName } from "@/lib/userUtils";
import { resolvePresence } from "@/lib/presence";
import { cn } from "@/lib/utils";
import { useOrgFilter } from "@/scripts/OrgFilterContext";
import { useOptionalDmSocket } from "@/scripts/DmSocketContext";

function isBotUser(user) {
  return Boolean(user?.is_bot || user?.user_id === "webhook");
}

function MemberProfileCardBody({
  user,
  presenceByUserId,
  onViewFullProfile,
  showPresence = true,
  viewProfileLabel = "View full profile",
}) {
  if (!user) return null;
  const presence = showPresence
    ? resolvePresence(user, presenceByUserId)
    : null;
  const name = getFullName(user) || user.display_name || "Member";
  const bot = isBotUser(user);

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-start gap-3">
        <OrgMemberAvatar
          user={user}
          className="h-14 w-14"
          fallbackClassName="text-base font-semibold"
        />
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="font-semibold text-sm leading-tight truncate">{name}</p>
          {user.title && !bot ? (
            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
              {user.title}
            </p>
          ) : null}
          {user.org_name ? (
            <p className="text-xs text-muted-foreground mt-0.5 truncate">
              {user.org_name}
            </p>
          ) : null}
          {presence ? (
            <p className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground">
              <span
                className={cn("h-2 w-2 rounded-full shrink-0", presence.dot)}
                aria-hidden
              />
              {presence.label}
            </p>
          ) : null}
        </div>
      </div>
      {user.org_email && !bot ? (
        <div className="rounded-md border bg-muted/40 px-2.5 py-2">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Email
          </p>
          <p className="text-xs mt-0.5 break-all">{user.org_email}</p>
        </div>
      ) : null}
      {onViewFullProfile ? (
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="w-full"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onViewFullProfile();
          }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {viewProfileLabel}
        </Button>
      ) : null}
    </div>
  );
}

/**
 * Hover (avatar) / focus-click (name) profile preview for org-scoped members.
 * Owns the full-profile modal open state.
 */
export default function MemberProfileHover({
  user,
  orgId: orgIdProp,
  presenceByUserId,
  children,
  className,
  side = "right",
  align = "start",
  openDelay = 200,
  closeDelay = 200,
  showPresence = true,
  allowFullProfile = true,
}) {
  const { selectedOrgId } = useOrgFilter();
  const dmSocket = useOptionalDmSocket();
  const [modalOpen, setModalOpen] = useState(false);
  const livePresence = presenceByUserId || dmSocket?.presenceByUserId;
  const orgScopedId =
    orgIdProp ||
    (selectedOrgId && selectedOrgId !== "social" ? selectedOrgId : null);
  // Partner-channel people live in another org, so the org-scoped profile
  // fetch would fail for them.
  const resolvedOrgId = allowFullProfile ? orgScopedId : null;

  if (!user?.user_id) {
    return children;
  }

  const bot = isBotUser(user);
  const webhookId = Number(user.webhook_id);
  const installationId = Number(user.installation_id);
  const canOpenWebhookProfile =
    bot && resolvedOrgId && Number.isFinite(webhookId) && webhookId > 0;
  const canOpenCircatronProfile =
    bot &&
    resolvedOrgId &&
    Number.isFinite(installationId) &&
    installationId > 0;
  const canOpenBotProfile = canOpenWebhookProfile || canOpenCircatronProfile;

  return (
    <>
      <HoverCard openDelay={openDelay} closeDelay={closeDelay}>
        <HoverCardTrigger asChild>
          <span
            data-member-profile-trigger
            className={cn(
              "inline-flex cursor-pointer rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              className
            )}
          >
            {children}
          </span>
        </HoverCardTrigger>
        <HoverCardContent side={side} align={align}>
          <MemberProfileCardBody
            user={user}
            presenceByUserId={livePresence}
            showPresence={showPresence}
            viewProfileLabel={bot ? "View bot profile" : "View full profile"}
            onViewFullProfile={
              bot
                ? canOpenBotProfile
                  ? () => setModalOpen(true)
                  : null
                : resolvedOrgId
                  ? () => setModalOpen(true)
                  : null
            }
          />
        </HoverCardContent>
      </HoverCard>
      {canOpenWebhookProfile ? (
        <WebhookBotProfileModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          orgId={resolvedOrgId}
          webhookId={webhookId}
          seedUser={user}
        />
      ) : null}
      {canOpenCircatronProfile && !canOpenWebhookProfile ? (
        <CircatronBotProfileModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          orgId={resolvedOrgId}
          installationId={installationId}
          seedUser={user}
        />
      ) : null}
      {!bot && resolvedOrgId ? (
        <MemberProfileModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          userId={user.user_id}
          orgId={resolvedOrgId}
          seedUser={user}
          presenceByUserId={livePresence}
        />
      ) : null}
    </>
  );
}
